import React from "react";
import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import { Calendar, Clock, Video } from "lucide-react";

const upcoming = [
  { week: "1st meeting", title: "Listing Presentation Mastery", coach: "S. Patel", date: "Virtual · 60 min", status: "live" },
  { week: "2nd meeting", title: "Negotiation Lab — Live Roleplay", coach: "M. Reed", date: "Virtual · 60 min", status: "upcoming" },
  { week: "Next month · 1st", title: "AI Tools for Agents Workshop", coach: "J. Okafor", date: "Virtual · 60 min", status: "upcoming" },
  { week: "Next month · 2nd", title: "Open House Conversion Bootcamp", coach: "L. Chen", date: "Virtual · 60 min", status: "upcoming" },
];

const recentReplays = [
  "Morning Power Hour — Lead Gen",
  "Closing Week Q&A — Top Producers",
  "Buyer Consult Scripts that Convert",
  "Database Reactivation Playbook",
];

export default function WeeklyTraining() {
  return (
    <section
      id="training"
      data-testid="training-section"
      className="relative py-24 sm:py-32 lg:py-40 border-t border-black/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 mb-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6"
        >
          <div>
            <div className="overline mb-4">Training</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#0a0a0a] max-w-2xl">
              Bi-weekly Zoom training.{" "}
              <em className="not-italic text-[#121275]">Recorded, searchable, free.</em>
            </h2>
          </div>
          <p className="max-w-md text-black/60 lg:text-right">
            Two Zoom sessions each month, taught by an active top producer. Every meeting is
            recorded and added to a searchable library — watch on demand whenever you want.
          </p>
        </motion.div>
      </div>

      {/* Hero session card */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          data-testid="training-hero-session"
          className="bg-[#f7f7f7] border border-[#121275]/30 p-8 sm:p-12 tracing-border relative overflow-hidden"
        >
          <div className="absolute top-6 right-6 flex items-center gap-2">
            <span className="w-2 h-2 bg-[#121275] rounded-full animate-pulse" />
            <span className="numeric text-[10px] uppercase tracking-widest text-[#121275]">
              This month
            </span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <div className="overline">Zoom Training Session</div>
              <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light text-[#0a0a0a] tracking-tighter mt-3">
                Live coaching, twice a month, on Zoom
              </h3>
              <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3 numeric text-xs uppercase tracking-widest text-black/60">
                <span className="flex items-center gap-2">
                  <Calendar size={14} className="text-[#121275]" /> Bi-weekly
                </span>
                <span className="flex items-center gap-2">
                  <Clock size={14} className="text-[#121275]" /> 60 min · Recorded
                </span>
                <span className="flex items-center gap-2">
                  <Video size={14} className="text-[#121275]" /> Zoom
                </span>
              </div>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <div className="numeric text-[10px] uppercase tracking-widest text-black/40">
                Bi-weekly
              </div>
              <div className="numeric text-6xl text-[#121275] tracking-tighter mt-1">
                24
              </div>
              <div className="numeric text-[10px] uppercase tracking-widest text-black/40 mt-1">
                Sessions / year
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Editorial ribbon marquee — replay library */}
      <div className="border-t border-b border-black/10 py-6 bg-[#f7f7f7] mt-20">
        <Marquee speed={28} gradient={false} pauseOnHover>
          {recentReplays.concat(recentReplays).map((title, i) => (
            <div key={i} className="flex items-center gap-6 px-10">
              <span className="numeric text-[#121275] text-xs uppercase tracking-widest">
                Replay
              </span>
              <span className="font-display text-xl lg:text-2xl text-[#0a0a0a] font-extralight">
                {title}
              </span>
              <span className="text-[#121275] text-xl">·</span>
            </div>
          ))}
        </Marquee>
      </div>
    </section>
  );
}
